// Sugestões de categoria do autocomplete ("Disjuntores em Elétrica"): as
// categorias mais frequentes entre os produtos encontrados. Puro.

import type { SearchCategorySuggestion } from "../../types/search.ts";
import { getCategoryHref } from "../routing/storefrontUrls.ts";

interface CategoryLike {
  id: number;
  name: string;
  slug: string;
  parent: number;
}

interface ProductLike {
  categories: Array<{ id: number; name: string; slug: string }>;
}

export function buildCategorySuggestions(
  products: readonly ProductLike[],
  allCategories: readonly CategoryLike[],
  limit = 3,
): SearchCategorySuggestion[] {
  const byId = new Map(allCategories.map((category) => [category.id, category]));
  const counts = new Map<number, number>();
  for (const product of products) {
    for (const category of new Set(product.categories.map((item) => item.id))) {
      counts.set(category, (counts.get(category) ?? 0) + 1);
    }
  }
  const parentIds = new Set(allCategories.map((category) => category.parent).filter((id) => id > 0));
  return [...counts.entries()]
    .map(([id, count]) => ({ category: byId.get(id), count }))
    .filter((entry): entry is { category: CategoryLike; count: number } => Boolean(entry.category))
    // Prefere a categoria mais específica: uma categoria-mãe só aparece se
    // não houver filhas suficientes.
    .sort((first, second) =>
      Number(parentIds.has(first.category.id)) - Number(parentIds.has(second.category.id))
      || second.count - first.count
      || first.category.name.localeCompare(second.category.name, "pt-BR"))
    .slice(0, limit)
    .map(({ category, count }) => {
      const parent = category.parent > 0 ? byId.get(category.parent) : undefined;
      return {
        name: category.name,
        ...(parent ? { parentName: parent.name } : {}),
        href: getCategoryHref(category, allCategories),
        count,
      };
    });
}

/** Partes do texto com o trecho digitado marcado (negrito no autocomplete). */
export function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const fold = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLocaleLowerCase("pt-BR");
  // Mapeia cada caractere do texto dobrado para a posição no original.
  const folded: string[] = [];
  const origin: number[] = [];
  [...text].forEach((character, index) => {
    for (const piece of fold(character)) {
      folded.push(piece);
      origin.push(index);
    }
  });
  const haystack = folded.join("");
  const chars = [...text];
  const marks = new Array(chars.length).fill(false);
  const words = [...new Set(fold(query).split(/[^a-z0-9,./]+/).filter((word) => word.length >= 2))];
  for (const word of words) {
    let from = 0;
    let found = haystack.indexOf(word, from);
    while (found !== -1) {
      // Só no começo de palavra.
      const before = found === 0 ? " " : haystack[found - 1];
      if (!/[a-z0-9]/.test(before)) {
        for (let position = found; position < found + word.length; position += 1) marks[origin[position]] = true;
      }
      from = found + word.length;
      found = haystack.indexOf(word, from);
    }
  }
  const parts: Array<{ text: string; match: boolean }> = [];
  chars.forEach((character, index) => {
    const last = parts[parts.length - 1];
    if (last && last.match === marks[index]) last.text += character;
    else parts.push({ text: character, match: marks[index] });
  });
  return parts;
}
